import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import type { User, AuthState, LoginCredentials } from "../types";
import { api } from "../utils/api";

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get<{ user: User }>("/api/auth/me");
      let user = response.user;

      try {
        const subResponse = await api.get<{ data: { plan: string; status: string; period_end: number } | null }>("/api/stripe/subscription");
        if (subResponse.data) {
          user = {
            ...user,
            subscription: {
              plan: subResponse.data.plan as "basic" | "premium" | "vip",
              status: subResponse.data.status as "active" | "inactive" | "past_due" | "cancelled",
              period_end: subResponse.data.period_end,
            },
          };
        }
      } catch {
        // Token invalid or storage unavailable; fall through to authenticated-false state
      }

      setState({
        user,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch {
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    const response = await api.post<Record<string, unknown>>(
      "/api/auth/login",
      credentials,
    );
    const { token, ...userData } = response;
    try {
      if (token) localStorage.setItem("auth_token", token as string);
    } catch {
      /* */
    }
    setState({
      user: userData as unknown as User,
      isAuthenticated: true,
      isLoading: false,
    });
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout", {});
    } catch {
      // Ignore logout errors
    } finally {
      try {
        localStorage.removeItem("auth_token");
      } catch {
        /* */
      }
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  }, []);

  useEffect(() => {
    // intentionally mount-only: refresh the auth state once on app start.
    // login/logout drive further state changes explicitly.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    checkAuth();
  }, [checkAuth]);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
